import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Trash2, RefreshCw, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Bulk test-user pruner — surfaces every account that matches a test/QA/
 * demo pattern (TEST_ prefix, @example.com email, "Tester" / "Demo Racer"
 * names, etc.) and lets the admin select + delete them in one shot.
 *
 * The backend already excludes admins from candidates, so admins can't
 * accidentally nuke a peer admin via this list.
 */
export default function AdminTestUserPruner({ onAfterPrune }) {
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/test-users/candidates`, {
        withCredentials: true,
      });
      const list = r.data.candidates || [];
      setCandidates(list);
      setSelected(new Set(list.map((c) => c.user_id)));  // default: all checked
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load candidates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === candidates.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(candidates.map((c) => c.user_id)));
    }
  };

  const prune = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const ok = window.confirm(
      `Delete ${ids.length} test user${ids.length === 1 ? "" : "s"} ` +
      `permanently? This cascades across users, sessions, lobbies, ` +
      `feedback, and queued catchphrases. Cannot be undone.`,
    );
    if (!ok) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/admin/users/bulk-delete`,
        { user_ids: ids },
        { withCredentials: true },
      );
      const { deleted, total } = r.data;
      toast.success(`Pruned ${deleted}/${total} test users`);
      await load();
      onAfterPrune?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBusy(false);
    }
  };

  const allChecked = candidates.length > 0 && selected.size === candidates.length;

  return (
    <section
      className="border border-white/10 bg-black/40 backdrop-blur-xl"
      data-testid="admin-test-user-pruner"
    >
      <div className="p-4 border-b border-white/10 flex items-center gap-3">
        <Sparkles className="w-4 h-4 text-[#FFCC00]" />
        <div className="flex-1">
          <div className="label-kicker">Auto-detected</div>
          <div className="font-heading text-xl uppercase">Test User Pruner</div>
        </div>
        <span
          className={`px-2 py-0.5 text-[10px] tracking-[0.25em] uppercase border ${
            candidates.length > 0
              ? "border-[#FF3B30] text-[#FF3B30] bg-[#FF3B30]/10"
              : "border-[#34C759]/50 text-[#34C759] bg-[#34C759]/5"
          }`}
          data-testid="admin-pruner-count"
        >
          {candidates.length} flagged
        </span>
        <button
          onClick={load}
          disabled={loading}
          data-testid="admin-pruner-refresh"
          className="p-1.5 border border-white/10 hover:border-white/30 transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {loading && candidates.length === 0 ? (
        <div className="p-8 text-center text-white/40 text-[11px] tracking-[0.3em] uppercase">
          Scanning…
        </div>
      ) : candidates.length === 0 ? (
        <div className="p-8 text-center text-white/50 text-sm flex flex-col items-center gap-2" data-testid="admin-pruner-clean">
          <ShieldCheck className="w-5 h-5 text-[#34C759]" />
          No test accounts detected. The leaderboard is clean.
        </div>
      ) : (
        <>
          <div className="px-4 py-2 border-b border-white/5 flex items-center gap-3 text-[11px] tracking-[0.25em] uppercase">
            <input
              type="checkbox"
              checked={allChecked}
              onChange={toggleAll}
              data-testid="admin-pruner-toggle-all"
              className="w-4 h-4 accent-[#FF3B30]"
            />
            <span className="text-white/60">
              {selected.size}/{candidates.length} selected
            </span>
            <span className="ml-auto text-white/30">user · email · reason</span>
          </div>
          <div className="divide-y divide-white/5 max-h-[40vh] overflow-y-auto">
            {candidates.map((c) => (
              <label
                key={c.user_id}
                className="flex items-start gap-3 px-4 py-3 hover:bg-white/5 cursor-pointer"
                data-testid={`admin-pruner-row-${c.user_id}`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(c.user_id)}
                  onChange={() => toggle(c.user_id)}
                  className="mt-1 w-4 h-4 accent-[#FF3B30]"
                  data-testid={`admin-pruner-check-${c.user_id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-sm uppercase tracking-tight truncate">
                    {c.name}
                    <span className="ml-2 text-[10px] text-white/40 font-mono normal-case tracking-normal">
                      ({c.user_id})
                    </span>
                  </div>
                  <div className="text-[11px] text-white/50 truncate">
                    {c.email}
                  </div>
                  <div className="text-[10px] text-[#FFCC00]/80 mt-0.5">
                    {c.reason} · {c.races} races · {c.currency.toLocaleString()}¢
                  </div>
                </div>
              </label>
            ))}
          </div>
          <div className="p-4 border-t border-white/10 flex items-center gap-3">
            <button
              onClick={prune}
              disabled={busy || selected.size === 0}
              data-testid="admin-pruner-prune-btn"
              className={`flex-1 px-4 py-2 border-2 font-heading uppercase text-sm tracking-wider flex items-center justify-center gap-2 transition-colors ${
                busy || selected.size === 0
                  ? "border-white/10 text-white/30 cursor-not-allowed"
                  : "border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white"
              }`}
            >
              <Trash2 className="w-4 h-4" />
              {busy ? "Pruning…" : `Delete ${selected.size} selected`}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
